import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Alert,
  Button,
  Box,
  Typography,
  Collapse,
  IconButton,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MailIcon from '@mui/icons-material/Mail';
import inviteService from '../../services/inviteService';

/**
 * InviteBanner - Dashboard notification for pending invites
 * 
 * Features:
 * - Shows count of pending invites
 * - Navigate to invites page
 * - Dismissable (session storage)
 * - Only shows for users with pending invites
 */
const InviteBanner = () => {
  const history = useHistory();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchPendingInvites = async () => {
      try {
        // Check if banner was dismissed in this session
        const isDismissed = sessionStorage.getItem('inviteBannerDismissed');
        if (isDismissed === 'true') {
          setDismissed(true);
          setLoading(false);
          return;
        }

        const response = await inviteService.getUserPendingInvites();
        setInvites(response.data || []);
      } catch (error) {
        console.error('Failed to fetch pending invites:', error);
        // Don't show banner on error
        setInvites([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPendingInvites();
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('inviteBannerDismissed', 'true');
  };

  const handleViewInvites = () => {
    // Navigate to MyInvitesPage
    history.push('/my-invites');
  };

  // Don't render if loading, dismissed, or no invites
  if (loading || dismissed || invites.length === 0) {
    return null;
  }

  return (
    <Collapse in={!dismissed}>
      <Alert
        severity="info"
        icon={<MailIcon />}
        sx={{
          mb: 3,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
        // Remove action prop, move its content inside the main layout for better alignment
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" fontWeight="medium">
              Bekleyen Davetleriniz Var!
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {invites.length === 1 ? (
                <>
                  <strong>{invites[0].companyId?.companyName}</strong> şirketinden bir davet aldınız.
                </>
              ) : (
                <>
                  <strong>{invites.length}</strong> şirketten davet aldınız.
                </>
              )}
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minWidth: 'fit-content',
          }}>
            <Chip
              label={`${invites.length} Davet`}
              color="primary"
              size="small"
              sx={{ mr: 1 }}
            />
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={handleViewInvites}
              sx={{ alignSelf: 'center', mt: '2px', /* slight nudge down for alignment */ }}
            >
              Görüntüle
            </Button>
            <IconButton
              aria-label="kapat"
              color="inherit"
              size="small"
              onClick={handleDismiss}
              sx={{ ml: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Alert>
    </Collapse>
  );
};

export default InviteBanner;

